import { useEffect, useState, useCallback } from 'react';
import { FileText, ArrowLeft } from 'lucide-react';
import { supabase } from '../services/supabase';
import { leerVarias, listaEnCastellano } from '../lib/leerVarias';
import { CATALOG, CATALOG_VERSION } from '../lib/evaluationCatalog';
import {
  tryBuildReportV2Model,
  scoreColor,
  type EvaluationReportV2,
} from '../lib/evaluationReportV2';
import { extractIntraPeriod, publishedForEvolution } from '../lib/evaluationSeries';
import EvaluationReportView, {
  type ReportNextObjectiveItem,
  type ReportObjectiveReviewItem,
  type ReportMetrics,
} from './EvaluationReportView';
import EvaluationEvolutionBetween from './EvaluationEvolutionBetween';
import type { ObjectiveStatus } from '../lib/evaluationObjectives';

// PR-EV2 (A3) / n=8 élite (PR-N8-3) — Ficha completa del alumno (tab de
// AlumnoDetallePage). Cabecera, asistencias, notas rápidas, objetivos y la lista
// de evaluaciones formales; desde cada una se abre la VISTA del informe n=8
// (radar 21 ejes + intra-periodo + tabla 3 niveles), sin descarga (D6).
// La evolución ENTRE informes (4 pilares + subgráficos) es N8-4.

interface Props {
  studentId: string;
}

interface Attendance {
  id: string;
  date: string;
  grupo: string;
  monitor: string;
  rating: number | null;
}
interface QuickNote {
  id: string;
  rating: number;
  note: string | null;
  date: string; // created_at (display)
  sessionDate: string | null; // fecha de la sesión (intra-periodo)
  trainerId: string | null;
  trainer: string;
}
interface FormalEval {
  id: string;
  report_number: string;
  period_start: string;
  period_end: string | null;
  // BUG 19: viaja para descartar los borradores del gráfico de evolución. Aquí
  // la RLS no ayuda —dirección y el entrenador SÍ los leen, y tienen que— así
  // que el descarte lo hace quien dibuja.
  status: string | null;
  monitor_name: string;
  monitor_id: string | null;
  overall_comment: string | null;
  catalog_version: number;
  position_primary: string | null;
  position_secondary: string | null;
  detailed_raw: unknown; // jsonb crudo (evolución entre informes, N8-4)
  model: EvaluationReportV2 | null; // null = catálogo no soportado (ver getCatalog)
}
interface Objective {
  id: string;
  pillar: string;
  description: string;
  status: ObjectiveStatus;
  evaluation_comment: string | null;
  created_at: string;
}
interface ReportData {
  metrics: ReportMetrics;
  intraPeriod: { date: string; rating: number }[];
  review: ReportObjectiveReviewItem[];
  next: ReportNextObjectiveItem[];
  /** Lo que no se pudo leer al abrir el informe. Ver el bloque de arriba. */
  fallidos: string[];
}

const PILLAR_LABEL: Record<string, string> = Object.fromEntries(
  CATALOG.map((p) => [p.key, p.label])
);
function pillarLabel(k: string): string {
  return PILLAR_LABEL[k] ?? k;
}

const STATUS_LABEL: Record<ObjectiveStatus, string> = {
  pending: 'Pendiente',
  achieved: 'Conseguido',
  partial: 'Parcial',
  not_achieved: 'No conseguido',
  maintained: 'Mantenido',
};
const STATUS_CLASS: Record<ObjectiveStatus, string> = {
  pending: 'badge-silver',
  achieved: 'badge-success',
  partial: 'badge-warning',
  not_achieved: 'badge-error',
  maintained: 'badge-gold',
};

function fmt(d: string | null) {
  if (!d) return '—';
  return new Date(`${d}T00:00:00`).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}
function ageFrom(birth: string | null): number | null {
  if (!birth) return null;
  const b = new Date(`${birth}T00:00:00`);
  const now = new Date();
  let a = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) a--;
  return a >= 0 && a < 120 ? a : null;
}

// ============================================================
// «SIN EVALUACIONES FORMALES» ES UNA AFIRMACIÓN SOBRE UN JUGADOR
// ============================================================
// Esta ficha es de la que se habla con la familia y con la que se decide si un
// chaval sube de grupo. Sus cinco bloques decían, cada uno con su frase:
//
//   «Sin evaluaciones formales.»   «Sin notas rápidas.»
//   «Sin asistencias registradas.» «Sin objetivos pendientes.»
//   «Sin objetivos cerrados.»
//
// Y las cinco frases se pintaban igual cuando la lectura fallaba, porque el
// `error` se caía en el destructuring y `data` llegaba a `null`, que es
// exactamente lo que devuelve una tabla vacía. Un entrenador que abre la ficha
// de un alumno con dos años de historia y lee «sin asistencias» no piensa que
// haya fallado nada: piensa que ese alumno no ha ido nunca.
//
// El peor no es ninguna de esas cinco: es el INFORME. `openReport` calcula
// «sesiones asistidas» y lo imprime como número. Sin poder leer las
// inscripciones el número no se quedaba en blanco, salía **0 sesiones
// asistidas** en un documento que se le da a un padre.
//
// Con `leerVarias` el `error` es el resultado y no una posición del patrón que
// se pueda omitir, y cada consulta trae su nombre para poder decir CUÁL no se
// pudo leer.
//
// Los objetivos se leían en una consulta aparte y SECUENCIAL, después de las
// otras cinco. Entran en la misma tanda: no dependen de nada de las anteriores,
// y así hay un solo camino de error en vez de dos.
//
// ⚠️ Las valoraciones de sesión (`session_ratings`) NO entran en el aviso, y es
//    deliberado: son la estrellita que adorna cada fila de asistencia. Si no se
//    pueden leer, la columna sale vacía y no se afirma nada que no sea verdad.
//    Lo que no puede pasar es que su fallo tape los otros.
const QUE = {
  perfil: 'el perfil del alumno',
  asistencias: 'las asistencias',
  notas: 'las notas rápidas',
  evaluaciones: 'las evaluaciones formales',
  objetivos: 'los objetivos',
  inscripciones: 'las sesiones del periodo',
  valoraciones: 'las valoraciones de sesión',
} as const;

/** Adornan; no afirman nada. Se leen y se mira su error, pero no avisan. */
const DECORATIVOS: string[] = [QUE.valoraciones];

/** Qué poner en un bloque vacío: no es lo mismo «no tiene» que «no se pudo leer». */
function textoVacio(que: string, fallidos: string[], sinDatos: string): string {
  return fallidos.includes(que) ? 'No se ha podido cargar.' : sinDatos;
}

export default function FichaAlumno({ studentId }: Props) {
  const [studentName, setStudentName] = useState('');
  const [age, setAge] = useState<number | null>(null);
  const [position, setPosition] = useState<string | null>(null);
  const [attendance, setAttendance] = useState<Attendance[]>([]);
  const [notes, setNotes] = useState<QuickNote[]>([]);
  const [evals, setEvals] = useState<FormalEval[]>([]);
  const [objectives, setObjectives] = useState<Objective[]>([]);
  const [loading, setLoading] = useState(true);
  const [bloquesFallidos, setBloquesFallidos] = useState<string[]>([]);

  // Vista del informe seleccionado.
  const [selected, setSelected] = useState<FormalEval | null>(null);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loadingReport, setLoadingReport] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { datos, fallidos } = await leerVarias([
        {
          que: QUE.perfil,
          consulta: supabase
            .from('profiles')
            .select('full_name, birth_date, position')
            .eq('id', studentId)
            .maybeSingle(),
        },
        {
          que: QUE.asistencias,
          consulta: supabase
            .from('enrollments')
            .select(
              'id, attendance, session:sessions!session_id(id, date, group:groups!group_id(name, monitor:profiles!monitor_id(full_name)))'
            )
            .eq('user_id', studentId)
            .eq('attendance', true),
        },
        {
          que: QUE.notas,
          consulta: supabase
            .from('quick_notes')
            .select(
              'id, rating, note, created_at, trainer_id, trainer:profiles!trainer_id(full_name), session:sessions!session_id(date)'
            )
            .eq('student_id', studentId)
            .order('created_at', { ascending: false }),
        },
        {
          que: QUE.evaluaciones,
          consulta: supabase
            .from('formal_evaluations')
            .select(
              'id, report_number, period_start, period_end, status, detailed_scores, catalog_version, overall_comment, monitor_id, position_primary, position_secondary, monitor:profiles!monitor_id(full_name)'
            )
            .eq('student_id', studentId)
            .order('period_start', { ascending: false }),
        },
        {
          // Adorna, no afirma: ver el ⚠️ de arriba. Fuera del aviso.
          que: QUE.valoraciones,
          consulta: supabase
            .from('session_ratings')
            .select('session_id, rating')
            .eq('user_id', studentId),
        },
        {
          que: QUE.objetivos,
          consulta: supabase
            .from('evaluation_objectives')
            .select('id, pillar, description, status, evaluation_comment, created_at')
            .eq('student_id', studentId)
            .order('created_at', { ascending: false }),
        },
      ]);
      if (cancelled) return;
      const [prof, enr, qn, fe, ratingsData, objs] = datos;
      setBloquesFallidos(fallidos.filter((f) => !DECORATIVOS.includes(f)));

      const profRow = prof as {
        full_name: string;
        birth_date: string | null;
        position: string | null;
      } | null;
      setStudentName(profRow?.full_name ?? '');
      setAge(ageFrom(profRow?.birth_date ?? null));
      setPosition(profRow?.position ?? null);

      const ratingBySession: Record<string, number> = {};
      ((ratingsData ?? []) as { session_id: string; rating: number }[]).forEach((r) => {
        ratingBySession[r.session_id] = r.rating;
      });

      const enrRows = (enr ?? []) as unknown as {
        id: string;
        session: {
          id: string;
          date: string;
          group: { name: string; monitor: { full_name: string } | null } | null;
        } | null;
      }[];
      setAttendance(
        enrRows
          .map((e) => ({
            id: e.id,
            date: e.session?.date ?? '',
            grupo: e.session?.group?.name ?? '—',
            monitor: e.session?.group?.monitor?.full_name ?? '—',
            rating: e.session ? (ratingBySession[e.session.id] ?? null) : null,
          }))
          .sort((a, b) => (a.date < b.date ? 1 : -1))
      );

      setNotes(
        (
          (qn ?? []) as unknown as {
            id: string;
            rating: number;
            note: string | null;
            created_at: string;
            trainer_id: string | null;
            trainer: { full_name: string } | null;
            session: { date: string } | null;
          }[]
        ).map((n) => ({
          id: n.id,
          rating: n.rating,
          note: n.note,
          date: n.created_at,
          sessionDate: n.session?.date ?? null,
          trainerId: n.trainer_id ?? null,
          trainer: n.trainer?.full_name ?? '—',
        }))
      );

      setEvals(
        (
          (fe ?? []) as unknown as {
            id: string;
            report_number: string;
            period_start: string;
            period_end: string | null;
            detailed_scores: unknown;
            catalog_version: number | null;
            overall_comment: string | null;
            monitor_id: string | null;
            position_primary: string | null;
            position_secondary: string | null;
            status: string | null;
            monitor: { full_name: string } | null;
          }[]
        ).map((e) => {
          const catalogVersion = e.catalog_version ?? 2;
          return {
            id: e.id,
            report_number: e.report_number,
            period_start: e.period_start,
            period_end: e.period_end,
            status: e.status,
            monitor_name: e.monitor?.full_name ?? '—',
            monitor_id: e.monitor_id ?? null,
            overall_comment: e.overall_comment,
            catalog_version: catalogVersion,
            position_primary: e.position_primary ?? null,
            position_secondary: e.position_secondary ?? null,
            detailed_raw: e.detailed_scores,
            model: tryBuildReportV2Model(e.detailed_scores, catalogVersion),
          };
        })
      );

      setObjectives((objs ?? []) as Objective[]);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId]);

  const activeObjectives = objectives.filter((o) => o.status === 'pending');
  const pastObjectives = objectives.filter((o) => o.status !== 'pending');

  const openReport = useCallback(
    async (e: FormalEval) => {
      setSelected(e);
      setLoadingReport(true);
      setReportData(null);
      const ps = e.period_start;
      const pe = e.period_end ?? e.period_start;

      // ⚠️ El informe IMPRIME «sesiones asistidas» como número. Sin las
      //    inscripciones ese número salía 0, no en blanco, en un documento que
      //    se le da a una familia. `metrics` pasa a admitir `null` para poder
      //    decir «—» en vez de inventar un cero.
      const { datos: dr, fallidos: frep } = await leerVarias([
        {
          que: QUE.inscripciones,
          consulta: supabase
            .from('enrollments')
            .select('attendance, session:sessions!session_id(date, status)')
            .eq('user_id', studentId),
        },
        {
          que: QUE.objetivos,
          consulta: supabase
            .from('evaluation_objectives')
            .select(
              'pillar, description, status, evaluation_comment, created_in_eval_id, evaluated_in_eval_id'
            )
            .or(`created_in_eval_id.eq.${e.id},evaluated_in_eval_id.eq.${e.id}`),
        },
      ]);
      const [enrAll, objs] = dr;
      const noHayInscripciones = frep.includes(QUE.inscripciones);

      const enrRows = (enrAll ?? []) as unknown as {
        attendance: boolean | null;
        session: { date: string; status: string } | null;
      }[];
      const periodRows = enrRows.filter(
        (r) =>
          r.session &&
          r.session.date >= ps &&
          r.session.date <= pe &&
          r.session.status !== 'cancelled'
      );
      // `null` es «no se ha podido contar», y es distinto de un 0 contado.
      const attended = noHayInscripciones
        ? null
        : periodRows.filter((r) => r.attendance === true).length;
      const total = periodRows.length;
      const attendancePct =
        noHayInscripciones || total === 0 ? null : Math.round(((attended ?? 0) / total) * 100);

      const intraPeriod = extractIntraPeriod(
        notes
          .filter(
            (n) =>
              n.trainerId === e.monitor_id &&
              n.sessionDate != null &&
              n.sessionDate >= ps &&
              n.sessionDate <= pe
          )
          .map((n) => ({ date: n.sessionDate as string, rating: n.rating }))
      );

      const objRows = (objs ?? []) as {
        pillar: string;
        description: string;
        status: ObjectiveStatus;
        evaluation_comment: string | null;
        created_in_eval_id: string;
        evaluated_in_eval_id: string | null;
      }[];
      const review: ReportObjectiveReviewItem[] = objRows
        .filter((o) => o.evaluated_in_eval_id === e.id)
        .map((o) => ({
          pillarLabel: pillarLabel(o.pillar),
          description: o.description,
          status: o.status,
          comment: o.evaluation_comment,
        }));
      const next: ReportNextObjectiveItem[] = objRows
        .filter((o) => o.created_in_eval_id === e.id)
        .map((o) => ({ pillarLabel: pillarLabel(o.pillar), description: o.description }));

      setReportData({
        metrics: { sessionsAttended: attended, attendancePct, totalEvaluations: evals.length },
        intraPeriod,
        review,
        next,
        fallidos: frep,
      });
      setLoadingReport(false);
    },
    [studentId, notes, evals.length]
  );

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-2 border-gold-primary/30 border-t-gold-primary rounded-full animate-spin" />
      </div>
    );
  }

  // ── Vista del informe seleccionado ──────────────────────────────
  if (selected) {
    const group = attendance[0]?.grupo ?? null;
    return (
      <div className="space-y-4" data-testid="ficha-alumno">
        <button
          onClick={() => {
            setSelected(null);
            setReportData(null);
          }}
          className="flex items-center gap-2 text-gold-primary font-inter text-sm hover:opacity-80"
        >
          <ArrowLeft size={16} /> Volver a la ficha
        </button>
        {loadingReport || !reportData ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-gold-primary/30 border-t-gold-primary rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {reportData.fallidos.length > 0 && (
              <p
                className="text-error text-sm bg-error/10 rounded p-2 mb-3"
                data-testid="ficha-informe-fallidos"
              >
                No se han podido cargar {listaEnCastellano(reportData.fallidos)}. Este informe está
                incompleto: no lo des por bueno ni lo descargues.
              </p>
            )}
            <EvaluationReportView
              reportNumber={selected.report_number}
              student={{ name: studentName, age, position, group }}
              trainerName={selected.monitor_name}
              period={{ start: selected.period_start, end: selected.period_end }}
              metrics={reportData.metrics}
              model={selected.model}
              catalogVersion={selected.catalog_version}
              overallComment={selected.overall_comment}
              intraPeriod={reportData.intraPeriod}
              objectivesReview={reportData.review}
              nextObjectives={reportData.next}
              fieldPosition={{
                primary: selected.position_primary,
                secondary: selected.position_secondary,
              }}
            />
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ficha-alumno">
      {/* Cabecera resumen.
          ⚠️ Estas tres son CIFRAS, y una cifra afirma que se ha contado. Con la
          lista sin leer, un `.length` de un array vacío daba «0 sesiones
          asistidas» en grande y en dorado. «—» dice lo único cierto. */}
      <div className="card flex flex-wrap gap-6">
        <div>
          <p className="label">Alumno</p>
          <p className="font-oswald text-xl text-white">{studentName}</p>
        </div>
        <div>
          <p className="label">Sesiones asistidas</p>
          <p className="font-oswald text-xl text-gold-primary" data-testid="ficha-kpi-asistencias">
            {bloquesFallidos.includes(QUE.asistencias) ? '—' : attendance.length}
          </p>
        </div>
        <div>
          <p className="label">Notas rápidas</p>
          <p className="font-oswald text-xl text-white" data-testid="ficha-kpi-notas">
            {bloquesFallidos.includes(QUE.notas) ? '—' : notes.length}
          </p>
        </div>
        <div>
          <p className="label">Evaluaciones formales</p>
          <p className="font-oswald text-xl text-white" data-testid="ficha-kpi-evaluaciones">
            {bloquesFallidos.includes(QUE.evaluaciones) ? '—' : evals.length}
          </p>
        </div>
      </div>

      {/* Alguna lista no se pudo leer. Se avisa arriba Y en el bloque: el aviso
          de arriba se ve al entrar, y el del bloque es el que lee quien baja
          hasta esa tabla. */}
      {bloquesFallidos.length > 0 && (
        <p
          className="text-error text-sm bg-error/10 rounded p-2"
          data-testid="ficha-bloques-fallidos"
        >
          No se han podido cargar {listaEnCastellano(bloquesFallidos)}. Lo que se ve de{' '}
          {bloquesFallidos.length === 1 ? 'ese apartado' : 'esos apartados'} puede estar incompleto.
        </p>
      )}

      {/* Objetivos activos + histórico */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card" data-testid="ficha-objetivos-activos">
          <h3 className="font-oswald text-sm uppercase tracking-wider text-white mb-3">
            Objetivos activos
          </h3>
          {activeObjectives.length === 0 ? (
            <p className="text-text-secondary text-sm" data-testid="ficha-vacio-obj-activos">
              {textoVacio(QUE.objetivos, bloquesFallidos, 'Sin objetivos pendientes.')}
            </p>
          ) : (
            <ul className="space-y-2">
              {activeObjectives.map((o) => (
                <li key={o.id} className="flex items-start gap-2 text-sm">
                  <span className="badge-silver shrink-0">{pillarLabel(o.pillar)}</span>
                  <span className="text-white">{o.description}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="card" data-testid="ficha-objetivos-historico">
          <h3 className="font-oswald text-sm uppercase tracking-wider text-white mb-3">
            Histórico de objetivos
          </h3>
          {pastObjectives.length === 0 ? (
            <p className="text-text-secondary text-sm" data-testid="ficha-vacio-obj-cerrados">
              {textoVacio(QUE.objetivos, bloquesFallidos, 'Sin objetivos cerrados.')}
            </p>
          ) : (
            <ul className="space-y-2">
              {pastObjectives.map((o) => (
                <li key={o.id} className="text-sm">
                  <div className="flex items-center gap-2">
                    <span className={`${STATUS_CLASS[o.status]} shrink-0`}>
                      {STATUS_LABEL[o.status]}
                    </span>
                    <span className="text-white">
                      [{pillarLabel(o.pillar)}] {o.description}
                    </span>
                  </div>
                  {o.evaluation_comment && (
                    <p className="text-text-secondary text-xs italic ml-1 mt-0.5">
                      {o.evaluation_comment}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Evaluaciones formales (lista + ver informe) */}
      <div className="card p-0 overflow-hidden">
        <div className="px-6 py-3 border-b border-bg-border bg-bg-elevated">
          <h3 className="font-oswald text-sm uppercase tracking-wider text-white">
            Evaluaciones formales
          </h3>
        </div>
        {evals.length === 0 ? (
          <p className="text-text-secondary text-sm p-6" data-testid="ficha-vacio-evaluaciones">
            {textoVacio(QUE.evaluaciones, bloquesFallidos, 'Sin evaluaciones formales.')}
          </p>
        ) : (
          <table className="w-full">
            <thead className="border-b border-bg-border bg-bg-elevated">
              <tr>
                <th className="table-th">Informe</th>
                <th className="table-th">Periodo</th>
                <th className="table-th">Entrenador</th>
                {CATALOG.map((p) => (
                  <th key={p.key} className="table-th" title={p.label}>
                    {p.label.slice(0, 3)}
                  </th>
                ))}
                <th className="table-th">Informe</th>
              </tr>
            </thead>
            <tbody data-testid="ficha-evals-list">
              {evals.map((e) => (
                <tr key={e.id} className="table-row">
                  <td className="table-td text-xs font-mono">{e.report_number}</td>
                  <td className="table-td text-text-secondary text-xs">
                    {fmt(e.period_start)} → {fmt(e.period_end)}
                  </td>
                  <td className="table-td text-text-secondary text-sm">{e.monitor_name}</td>
                  {/* LAS COLUMNAS SON LAS DEL CATÁLOGO VIGENTE, y eso aquí no
                      se puede evitar: es una tabla con cabecera fija. Lo que no
                      vale es pintar un guion por columna para un informe de
                      otra versión, que se lee como «no tiene notas» teniéndolas.
                      Se dice lo que es. */}
                  {e.model && e.model.catalogVersion === CATALOG_VERSION ? (
                    CATALOG.map((p) => {
                      const sc = e.model!.pillars.find((pp) => pp.key === p.key)?.score ?? null;
                      return (
                        <td key={p.key} className="table-td">
                          <span className="font-oswald" style={{ color: scoreColor(sc) }}>
                            {sc != null ? sc.toFixed(1) : '—'}
                          </span>
                        </td>
                      );
                    })
                  ) : (
                    <td
                      className="table-td text-text-secondary text-xs"
                      colSpan={CATALOG.length}
                      data-testid={`ficha-eval-otro-catalogo-${e.id}`}
                    >
                      Catálogo v{e.catalog_version} — sin medias comparables
                    </td>
                  )}
                  <td className="table-td">
                    <button
                      onClick={() => openReport(e)}
                      data-testid={`ficha-ver-${e.id}`}
                      className="flex items-center gap-1 text-gold-primary hover:text-gold-light text-sm"
                      title="Ver informe"
                    >
                      <FileText size={16} /> Ver
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Evolución ENTRE informes (4 pilares + 4 subgráficos, gating <2) — N8-4 */}
      {evals.length > 0 && (
        <EvaluationEvolutionBetween
          evals={publishedForEvolution(
            evals.map((e) => ({
              period_start: e.period_start,
              report_number: e.report_number,
              detailed_scores: e.detailed_raw,
              status: e.status,
            }))
          )}
        />
      )}

      {/* Notas rápidas cronológico */}
      <div className="card p-0 overflow-hidden">
        <div className="px-6 py-3 border-b border-bg-border bg-bg-elevated">
          <h3 className="font-oswald text-sm uppercase tracking-wider text-white">
            Notas rápidas (todos los entrenadores)
          </h3>
        </div>
        {notes.length === 0 ? (
          <p className="text-text-secondary text-sm p-6" data-testid="ficha-vacio-notas">
            {textoVacio(QUE.notas, bloquesFallidos, 'Sin notas rápidas.')}
          </p>
        ) : (
          <table className="w-full">
            <thead className="border-b border-bg-border bg-bg-elevated">
              <tr>
                <th className="table-th">Fecha</th>
                <th className="table-th">Entrenador</th>
                <th className="table-th">Nota</th>
                <th className="table-th">Comentario</th>
              </tr>
            </thead>
            <tbody data-testid="ficha-notes-list">
              {notes.map((n) => (
                <tr key={n.id} className="table-row">
                  <td className="table-td text-text-secondary text-xs">
                    {fmt(n.date.slice(0, 10))}
                  </td>
                  <td className="table-td text-text-secondary text-sm">{n.trainer}</td>
                  <td className="table-td">
                    <span className="font-oswald" style={{ color: scoreColor(n.rating) }}>
                      {n.rating}/10
                    </span>
                  </td>
                  <td className="table-td text-text-secondary text-sm">{n.note ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Asistencias cronológico */}
      <div className="card p-0 overflow-hidden">
        <div className="px-6 py-3 border-b border-bg-border bg-bg-elevated">
          <h3 className="font-oswald text-sm uppercase tracking-wider text-white">Asistencias</h3>
        </div>
        {attendance.length === 0 ? (
          <p className="text-text-secondary text-sm p-6" data-testid="ficha-vacio-asistencias">
            {textoVacio(QUE.asistencias, bloquesFallidos, 'Sin asistencias registradas.')}
          </p>
        ) : (
          <table className="w-full">
            <thead className="border-b border-bg-border bg-bg-elevated">
              <tr>
                <th className="table-th">Fecha</th>
                <th className="table-th">Grupo</th>
                <th className="table-th">Entrenador</th>
                <th className="table-th">Su valoración</th>
              </tr>
            </thead>
            <tbody data-testid="ficha-attendance-list">
              {attendance.map((a) => (
                <tr key={a.id} className="table-row">
                  <td className="table-td text-text-secondary text-xs">{fmt(a.date)}</td>
                  <td className="table-td text-sm">{a.grupo}</td>
                  <td className="table-td text-text-secondary text-sm">{a.monitor}</td>
                  <td className="table-td text-sm">{a.rating != null ? `${a.rating}★` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
